import { queryOptions, mutationOptions } from '@tanstack/react-query';
import { apiRequestV2 } from '@/lib/queryClient';
import { codeVerificationKeys } from '@/constant/queryKeys';
import { useAuthStore } from '@/hooks/useAuthStore';

export interface CodeVerificationStatusResponse {
  verified: boolean;
  requiresVerification: boolean;
}

export interface VerifyCodeRequest {
  projectId: number;
  code: string;
}

export interface VerifyCodeResponse {
  success: boolean;
  message: string;
}

/**
 * Get verification status for a project
 * @param projectId - The project ID to check verification status for
 * @returns Query options for verification status
 */
export function getCodeVerificationStatusOptions(projectId?: number) {
  const { isAuthenticated } = useAuthStore();

  return queryOptions({
    queryKey: codeVerificationKeys.status(projectId),
    queryFn: () =>
      apiRequestV2<CodeVerificationStatusResponse>(
        'GET',
        `/api/v1/code-verification/project/${projectId}/status`
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!projectId && isAuthenticated,
  });
}

/**
 * Verify code for a project
 * @returns Mutation options for code verification
 */
export function verifyCodeOptions() {
  return mutationOptions({
    mutationKey: codeVerificationKeys.verify,
    mutationFn: (request: VerifyCodeRequest) =>
      apiRequestV2<VerifyCodeResponse>(
        'POST',
        `/api/v1/code-verification/project/${request.projectId}/verify`,
        { code: request.code }
      ),
  });
}
